import { HexResources, type ResourceData } from '@/components/HexResources';

interface BuildingBonus {
    level: number;
    level_bonus: number;
    assigned_count: number;
    character_bonus: number;
    total_bonus: number;
}

interface ResourcesCollectedModalProps {
    isOpen: boolean;
    onClose: () => void;
    collectedResources: ResourceData;
    collectedHours: number;
    collectedBaseResources: ResourceData | null;
    showBreakdown: boolean;
    setShowBreakdown: (show: boolean) => void;
    lumberMillBonus: BuildingBonus | null;
    herbalistHutBonus: BuildingBonus | null;
    teaHouseBonus: BuildingBonus | null;
    shrineBonus: BuildingBonus | null;
    smokehouseBonus: BuildingBonus | null;
}

export function ResourcesCollectedModal({
    isOpen,
    onClose,
    collectedResources,
    collectedHours,
    collectedBaseResources,
    showBreakdown,
    setShowBreakdown,
    lumberMillBonus,
    herbalistHutBonus,
    teaHouseBonus,
    shrineBonus,
    smokehouseBonus,
}: ResourcesCollectedModalProps) {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg p-6 w-full max-w-lg border border-slate-700 max-h-[90vh] overflow-y-auto">
                <h3 className="text-lg font-semibold text-white mb-4">Resources Collected!</h3>
                <div className="mb-4">
                    <p className="text-gray-300 mb-3">You collected the following resources:</p>
                    <div className="bg-green-900/30 border border-green-500/50 rounded-lg p-3">
                        <HexResources resources={collectedResources} compact showHeaders />
                    </div>
                </div>

                {/* Expandable Calculation Breakdown */}
                {collectedHours > 0 && collectedBaseResources && (
                    <div className="mb-4">
                        <button
                            type="button"
                            onClick={() => setShowBreakdown(!showBreakdown)}
                            className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-300 transition-colors"
                        >
                            <span className={`transform transition-transform ${showBreakdown ? 'rotate-90' : ''}`}>▶</span>
                            <span>How was this calculated? ({collectedHours} hour{collectedHours !== 1 ? 's' : ''})</span>
                        </button>

                        {showBreakdown && (
                            <div className="mt-3 bg-slate-700/50 border border-slate-600 rounded-lg p-4">
                                <p className="text-xs text-gray-400 mb-3">
                                    Resources decrease by 1 each hour (minimum 1). After 24 hours, there's a 50% chance each hour produces nothing. After 48 hours, there's a 75% chance.
                                </p>

                                {/* Base Resources */}
                                <div className="mb-3">
                                    <h4 className="text-xs font-medium text-gray-300 mb-1">Base Resources (per hour):</h4>
                                    <div className="text-xs text-gray-400">
                                        Tea: {collectedBaseResources.tea}, Wood: {collectedBaseResources.wood}, Mystical: {collectedBaseResources.mystical}, Medicinal: {collectedBaseResources.medicinal}, Edible: {collectedBaseResources.edible}
                                    </div>
                                </div>

                                {/* Hour-by-Hour Breakdown (first 48 hours) */}
                                <h4 className="text-xs font-medium text-gray-300 mb-2">Hour-by-hour breakdown:</h4>
                                <div className="max-h-64 overflow-y-auto">
                                    <table className="w-full text-xs">
                                        <thead className="sticky top-0 bg-slate-700">
                                            <tr className="text-gray-400 border-b border-slate-600">
                                                <th className="py-1 text-left">Hour</th>
                                                <th className="py-1 text-right">Tea</th>
                                                <th className="py-1 text-right">Wood</th>
                                                <th className="py-1 text-right">Myst</th>
                                                <th className="py-1 text-right">Med</th>
                                                <th className="py-1 text-right">Edible</th>
                                                <th className="py-1 text-right">Note</th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-gray-300">
                                            {Array.from({ length: Math.min(collectedHours, 48) }, (_, i) => {
                                                const hour = i + 1;
                                                const decrement = hour - 1;
                                                const tea = Math.max(1, collectedBaseResources.tea - decrement);
                                                const wood = Math.max(1, collectedBaseResources.wood - decrement);
                                                const mystical = Math.max(1, collectedBaseResources.mystical - decrement);
                                                const medicinal = Math.max(1, collectedBaseResources.medicinal - decrement);
                                                const edible = Math.max(1, collectedBaseResources.edible - decrement);
                                                const note = hour > 48 ? '75% skip' : hour > 24 ? '50% skip' : '';
                                                return (
                                                    <tr key={hour} className={`border-b border-slate-600/50 ${hour > 24 ? 'opacity-75' : ''}`}>
                                                        <td className="py-1">{hour}</td>
                                                        <td className="py-1 text-right">{tea}</td>
                                                        <td className="py-1 text-right">{wood}</td>
                                                        <td className="py-1 text-right">{mystical}</td>
                                                        <td className="py-1 text-right">{medicinal}</td>
                                                        <td className="py-1 text-right">{edible}</td>
                                                        <td className="py-1 text-right text-yellow-400">{note}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {collectedHours > 48 && (
                                    <p className="mt-2 text-xs text-gray-400 italic">
                                        ... and {collectedHours - 48} more hour{collectedHours - 48 !== 1 ? 's' : ''} with 75% skip chance each.
                                    </p>
                                )}

                                {/* Building Bonuses Section */}
                                {(lumberMillBonus || herbalistHutBonus || teaHouseBonus || shrineBonus || smokehouseBonus) && (
                                    <div className="mt-4 pt-3 border-t border-slate-600">
                                        <h4 className="text-xs font-medium text-gray-300 mb-2">Building Bonuses:</h4>
                                        <div className="space-y-2">
                                            {lumberMillBonus && lumberMillBonus.total_bonus > 0 && (
                                                <BuildingBonusRow
                                                    icon="🪵"
                                                    name="Lumber Mill"
                                                    bonus={lumberMillBonus}
                                                    resourceName="Wood"
                                                    colorClass="bg-green-900/30 border-green-700/50 text-green-300"
                                                />
                                            )}
                                            {herbalistHutBonus && herbalistHutBonus.total_bonus > 0 && (
                                                <BuildingBonusRow
                                                    icon="🌿"
                                                    name="Herbalist Hut"
                                                    bonus={herbalistHutBonus}
                                                    resourceName="Medicinal"
                                                    colorClass="bg-green-900/30 border-green-700/50 text-green-300"
                                                />
                                            )}
                                            {teaHouseBonus && teaHouseBonus.total_bonus > 0 && (
                                                <BuildingBonusRow
                                                    icon="🍵"
                                                    name="Tea House"
                                                    bonus={teaHouseBonus}
                                                    resourceName="Tea"
                                                    colorClass="bg-green-900/30 border-green-700/50 text-green-300"
                                                />
                                            )}
                                            {shrineBonus && shrineBonus.total_bonus > 0 && (
                                                <BuildingBonusRow
                                                    icon="✨"
                                                    name="Shrine"
                                                    bonus={shrineBonus}
                                                    resourceName="Mystical"
                                                    colorClass="bg-purple-900/30 border-purple-700/50 text-purple-300"
                                                />
                                            )}
                                            {smokehouseBonus && smokehouseBonus.total_bonus > 0 && (
                                                <BuildingBonusRow
                                                    icon="🥓"
                                                    name="Smokehouse"
                                                    bonus={smokehouseBonus}
                                                    resourceName="Edible"
                                                    colorClass="bg-orange-900/30 border-orange-700/50 text-orange-300"
                                                />
                                            )}
                                        </div>
                                    </div>
                                )}

                                <p className="mt-3 text-xs text-gray-500 italic">
                                    Note: Actual amounts may vary due to random depletion chances after 24 hours.
                                </p>
                            </div>
                        )}
                    </div>
                )}

                <div className="flex justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                    >
                        Awesome!
                    </button>
                </div>
            </div>
        </div>
    );
}

interface BuildingBonusRowProps {
    icon: string;
    name: string;
    bonus: BuildingBonus;
    resourceName: string;
    colorClass: string;
}

function BuildingBonusRow({ icon, name, bonus, resourceName, colorClass }: BuildingBonusRowProps) {
    const [bgColor, borderColor, textColor] = colorClass.split(' ');
    return (
        <div className={`${bgColor} border ${borderColor} rounded p-2`}>
            <div className="flex items-center gap-2 text-xs">
                <span className="text-lg">{icon}</span>
                <div>
                    <span className={`${textColor} font-medium`}>{name} (L{bonus.level})</span>
                    <span className="text-gray-400 ml-2">+{bonus.total_bonus} {resourceName}</span>
                </div>
            </div>
            <div className="text-xs text-gray-500 mt-1 ml-7">
                Level bonus: +{bonus.level_bonus} | Worker rolls: +{bonus.character_bonus} ({bonus.assigned_count} worker{bonus.assigned_count !== 1 ? 's' : ''})
            </div>
        </div>
    );
}
